const UUID=/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const KEY=/^op-aplus-public-v2:([a-f0-9]{64}):([0-9a-f-]{36}):/;
const PREFIX='op-aplus-late-notice-v1:';
const API='https://bv7uxyxmxktg3ofvnpviufykse0igoth.lambda-url.ap-southeast-1.on.aws';

// This reads our callback/fulfillment journal. It never invokes a provider
// status check, creates a payment, changes an order, or awards any currency.
export function createAplusLateNotices(options={}) {
    const storage=options.storage,clock=options.now||(()=>Date.now());
    const later=options.setTimeout||setTimeout,clear=options.clearTimeout||clearTimeout;
    const memory=new Map();let current=null,timer=null,running=false,epoch=0,controller=null;
    function read(key){try{const value=storage&&storage.getItem(key);if(value)return JSON.parse(value);}catch(_){}return memory.get(key)||null;}
    function write(key,value){memory.set(key,value);try{if(storage)storage.setItem(key,JSON.stringify(value));}catch(_){}return value;}
    function namespace(activeKey){const found=KEY.exec(activeKey||'');return found&&UUID.test(found[2])?PREFIX+found[1]+':'+found[2].toLowerCase()+':':null;}
    function validContext(value){return value.progressiveActivityId===undefined&&value.progressiveStep===undefined||
        typeof value.progressiveActivityId==='string'&&/^[A-Za-z0-9_-]{1,100}$/.test(value.progressiveActivityId)&&Number.isSafeInteger(value.progressiveStep)&&value.progressiveStep>=1&&value.progressiveStep<=30;}
    function entries(prefix){
        const keys=new Set([...memory.keys()].filter(key=>key.startsWith(prefix)));
        try{if(storage)for(let i=0;i<storage.length;i++){const key=storage.key(i);if(key&&key.startsWith(prefix))keys.add(key);}}catch(_){}
        return [...keys].map(key=>({key,value:read(key)})).filter(({value})=>value&&value.version===1&&UUID.test(value.requestId||'')&&
            typeof value.productId==='string'&&value.productId.length>0&&value.productId.length<=200&&validContext(value));
    }
    function register({key,intent,owner,order}){
        const prefix=namespace(key);
        if(!prefix||!intent||!UUID.test(intent.requestId||'')||!intent.productId||!validContext(intent))return false;
        // A visible credited result already told the user; closing it must not
        // produce a second success notice from this background tracker.
        if(order&&order.status==='paid'&&order.fulfillmentStatus==='credited'){acknowledge(key,intent.requestId);return true;}
        const recordKey=prefix+intent.requestId,previous=read(recordKey);
        if(previous&&previous.version===1)return true;
        const userId=owner&&owner.userId;
        const value={version:1,requestId:intent.requestId,productId:intent.productId,createdAt:clock(),nextAt:clock(),attempts:0};
        if(UUID.test(userId||''))value.userId=userId.toLowerCase();
        if(intent.progressiveActivityId!==undefined){value.progressiveActivityId=intent.progressiveActivityId;value.progressiveStep=intent.progressiveStep;}
        write(recordKey,value);wake();return true;
    }
    function migrateDismissed(digest,gameId){
        const activePrefix='op-aplus-public-v2:'+digest+':'+gameId+':';
        try{if(storage)for(let i=0;i<storage.length;i++){
            const key=storage.key(i);if(!key||!key.startsWith(activePrefix)||!key.endsWith(':last-dismissed'))continue;
            const saved=read(key),parts=key.slice(activePrefix.length,-':last-dismissed'.length).split(':');
            if(!saved||!UUID.test(saved.requestId||'')||saved.productId!==parts[0]||![1,3].includes(parts.length))continue;
            const intent={requestId:saved.requestId,productId:saved.productId};
            if(parts.length===3){intent.progressiveActivityId=decodeURIComponent(parts[1]);intent.progressiveStep=Number(parts[2]);}
            if(validContext(intent))register({key:key.slice(0,-':last-dismissed'.length),intent});
        }}catch(_){/* Old local records are optional; new dismissals register directly. */}
    }
    async function legacyEntry(input,seen){
        const {session,gameId,transactionId,productId,providerId}=input||{},token=session&&session.accessToken,userId=session&&session.userId;
        if(!UUID.test(token||'')||!UUID.test(gameId||'')||!UUID.test(transactionId||'')||typeof productId!=='string'||!productId||
            !['antpay','antpay3'].includes(providerId)||userId&&!UUID.test(userId))return false;
        try{
            const digest=await options.digest(token);if(!/^[a-f0-9]{64}$/.test(digest))return false;
            const recordKey=PREFIX+digest+':'+gameId.toLowerCase()+':'+transactionId,previous=read(recordKey);
            if(previous&&(previous.noticeKind!=='legacy'||previous.productId!==productId||previous.providerId!==providerId))return false;
            if(previous&&!seen)return true;
            const value=previous||{version:1,noticeKind:'legacy',requestId:transactionId,productId,providerId,createdAt:clock(),nextAt:clock(),attempts:0};
            if(userId)value.userId=userId.toLowerCase();
            if(seen)value.seenAt=clock()||1;
            write(recordKey,value);if(!seen)wake();return true;
        }catch(_){return false;}
    }
    function acknowledge(key,requestId){
        const prefix=namespace(key);if(!prefix||!UUID.test(requestId||''))return;
        const recordKey=prefix+requestId,value=read(recordKey);
        if(value&&!value.seenAt)write(recordKey,{...value,seenAt:clock()||1});
    }
    function matches(order,record,scope){
        return order&&order.contractVersion===3&&order.environment==='PROD'&&order.walletMode==='production'&&order.testOnly===false&&
            order.owner&&UUID.test(order.owner.userId||'')&&order.owner.gameId===scope.gameId&&
            (!scope.userId||order.owner.userId===scope.userId)&&(!record.userId||order.owner.userId===record.userId)&&
            order.requestId===record.requestId&&order.productId===record.productId&&
            order.progressiveActivityId===record.progressiveActivityId&&order.progressiveStep===record.progressiveStep&&
            order.currency==='HKD'&&Number.isSafeInteger(order.amountMinor)&&order.amountMinor>=4700&&order.amountMinor<=117000&&
            Number.isSafeInteger(order.paidGold)&&order.paidGold>0&&Number.isSafeInteger(order.freeGold)&&order.freeGold>=0&&
            Number.isSafeInteger(order.paidGold+order.freeGold);
    }
    function matchesLegacy(order,record,scope){
        return order&&order.contractVersion===3&&order.environment==='PROD'&&order.walletMode==='production'&&order.testOnly===false&&
            order.owner&&UUID.test(order.owner.userId||'')&&order.owner.gameId===scope.gameId&&
            (!scope.userId||order.owner.userId===scope.userId)&&(!record.userId||order.owner.userId===record.userId)&&
            order.transactionId===record.requestId&&order.productId===record.productId&&order.providerId===record.providerId&&
            order.status==='paid'&&['activated','credited'].includes(order.fulfillmentStatus);
    }
    function schedule(){
        clear(timer);timer=null;
        if(!current||current.denied||!isVisible())return;
        const pending=entries(current.prefix).filter(({value})=>!value.seenAt);
        if(!pending.length)return;
        const next=Math.min(...pending.map(({value})=>Number.isFinite(value.nextAt)?value.nextAt:clock()));
        timer=later(()=>poll(),Math.max(1000,Math.min(900000,next-clock())));
    }
    function isVisible(){return !options.visible||options.visible();}
    async function poll(){
        clear(timer);timer=null;
        if(!current||current.denied||running||!isVisible())return;
        running=true;const scope=current,turn=epoch;
        try{
            const due=entries(scope.prefix).filter(({value})=>!value.seenAt&&(!Number.isFinite(value.nextAt)||value.nextAt<=clock()))
                .sort((a,b)=>(a.value.nextAt||0)-(b.value.nextAt||0)).slice(0,3);
            for(const {key,value} of due){
                if(turn!==epoch||scope!==current||!isVisible())break;
                const latest=read(key);if(!latest||latest.seenAt)continue;
                const attempts=(latest.attempts||0)+1;
                write(key,{...latest,attempts,nextAt:clock()+[15000,30000,60000,120000,300000,900000][Math.min(attempts-1,5)]});
                controller=new AbortController();const timeout=later(()=>controller&&controller.abort(),15000);
                try{
                    const response=await options.fetch(API+(value.noticeKind==='legacy'?'/v3/legacy-orders/':'/v3/orders/')+value.requestId,{method:'GET',mode:'cors',credentials:'omit',cache:'no-store',redirect:'error',
                        headers:{Authorization:scope.token,'Content-Type':'application/json'},signal:controller.signal});
                    if(turn!==epoch||scope!==current)break;
                    if(response.status===401||response.status===403){scope.denied=true;break;}
                    if(!response.ok)continue;
                    const order=await response.json();
                    if(turn!==epoch||scope!==current)continue;
                    const legacy=value.noticeKind==='legacy';
                    if(legacy?!matchesLegacy(order,value,scope):!matches(order,value,scope)||order.status!=='paid'||order.fulfillmentStatus!=='credited'||!UUID.test(order.transactionId||''))continue;
                    const publish=()=>{
                        if(turn!==epoch||scope!==current)return;
                        const saved=read(key);if(!saved||saved.seenAt)return;
                        if(!isVisible()){write(key,{...saved,nextAt:clock()});return;}
                        // Persist before publishing; Web Locks also serialize
                        // this acknowledgement across tabs where supported.
                        write(key,{...saved,seenAt:clock()||1});
                        scope.notify({requestId:value.requestId,transactionId:order.transactionId,merchantOrderId:order.merchantOrderId||'',
                            providerId:legacy?value.providerId:'apluspay',
                            totalGold:legacy?undefined:order.paidGold+order.freeGold,
                            amountMinor:legacy?undefined:order.amountMinor,
                            scheduled:legacy?order.fulfillmentStatus==='activated':order.fulfillmentMode==='scheduled'||['card','vip-card'].includes(order.productKind)});
                    };
                    if(options.withLock)await options.withLock(key,publish);else publish();
                }catch(_){/* Retry only our journal, with backoff and while visible. */}
                finally{clear(timeout);controller=null;}
            }
        }finally{running=false;schedule();}
    }
    function wake(){if(current&&!current.denied&&isVisible())poll();}
    function stop(){epoch++;current=null;clear(timer);timer=null;if(controller)controller.abort();controller=null;}
    async function watch({session,gameId},notify){
        stop();const turn=epoch,token=session&&session.accessToken,userId=session&&session.userId;
        if(!UUID.test(token||'')||!UUID.test(gameId||'')||userId&&!UUID.test(userId))return;
        try{
            const bytes=await options.digest(token);
            if(turn!==epoch||!/^[a-f0-9]{64}$/.test(bytes))return;
            migrateDismissed(bytes,gameId.toLowerCase());
            current={token,userId:userId&&userId.toLowerCase(),gameId:gameId.toLowerCase(),prefix:PREFIX+bytes+':'+gameId.toLowerCase()+':',notify,denied:false};
            await poll();
        }catch(_){/* Lack of local tracking must never change payment behavior. */}
    }
    return {register,acknowledge,registerLegacy:input=>legacyEntry(input,false),acknowledgeLegacy:input=>legacyEntry(input,true),watch,stop,wake,poll};
}

let singleton;
export function aplusLateNotices(){
    if(!singleton){
        let storage;try{if(typeof window!=='undefined')storage=window.localStorage;}catch(_){}
        singleton=createAplusLateNotices({storage,fetch:(...args)=>fetch(...args),
            withLock:(key,publish)=>typeof navigator!=='undefined'&&navigator.locks&&navigator.locks.request
                ?navigator.locks.request(key,publish):publish(),
            visible:()=>typeof document!=='undefined'&&document.visibilityState!=='hidden',
            digest:async token=>{
                const bytes=await window.crypto.subtle.digest('SHA-256',new TextEncoder().encode(token));
                return Array.from(new Uint8Array(bytes),value=>value.toString(16).padStart(2,'0')).join('');
            }});
    }
    return singleton;
}
